import { supabase } from './supabase';

export interface PatientCasePicture {
  id: string;
  patient_id: string;
  picture_url: string;
  picture_type: 'case' | 'report';
  upload_date: string;
  notes?: string;
  uploaded_by?: string;
  created_at: string;
  updated_at: string;
}

export interface PatientReportPicture {
  id: string;
  patient_id: string;
  report_date: string;
  picture_url: string;
  notes?: string;
  uploaded_by?: string;
  created_at: string;
  updated_at: string;
}

export interface BulkSMSHistory {
  id: string;
  send_date: string;
  total_numbers: number;
  sms_count: number;
  message: string;
  sender_name?: string;
  sender_id?: string;
  recipients: any[];
  status: 'sent' | 'pending' | 'failed';
  created_at: string;
}

export interface SMSRecipient {
  id: string;
  bulk_sms_id: string;
  patient_id?: string;
  phone_number: string;
  patient_name?: string;
  message: string;
  status: 'sent' | 'pending' | 'failed';
  sent_at: string;
  created_at: string;
}

export interface PatientReportCheck {
  id: string;
  patient_id: string;
  check_date: string;
  report_type?: string;
  status: 'pending' | 'completed' | 'cancelled';
  notes?: string;
  checked_by?: string;
  created_at: string;
  updated_at: string;
}

export const casePictureService = {
  async getAll(patientId?: string): Promise<PatientCasePicture[]> {
    let query = supabase
      .from('patient_case_pictures')
      .select('*')
      .order('upload_date', { ascending: false });
    
    if (patientId) {
      query = query.eq('patient_id', patientId);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async create(picture: Partial<PatientCasePicture>): Promise<PatientCasePicture> {
    const { data, error } = await supabase
      .from('patient_case_pictures')
      .insert(picture)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, updates: Partial<PatientCasePicture>): Promise<PatientCasePicture> {
    const { data, error } = await supabase
      .from('patient_case_pictures')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('patient_case_pictures')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  async uploadPicture(file: File, patientId: string): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${patientId}/${Date.now()}.${fileExt}`;
    
    const { data, error } = await supabase.storage
      .from('patient-pictures')
      .upload(fileName, file);
    
    if (error) throw error;
    
    const { data: urlData } = supabase.storage
      .from('patient-pictures')
      .getPublicUrl(fileName);
    
    return urlData.publicUrl;
  }
};

export const reportPictureService = {
  async getAll(patientId?: string, startDate?: string, endDate?: string): Promise<PatientReportPicture[]> {
    let query = supabase
      .from('patient_report_pictures')
      .select('*')
      .order('report_date', { ascending: false });
    
    if (patientId) {
      query = query.eq('patient_id', patientId);
    }
    
    if (startDate) {
      query = query.gte('report_date', startDate);
    }
    
    if (endDate) {
      query = query.lte('report_date', endDate);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async create(picture: Partial<PatientReportPicture>): Promise<PatientReportPicture> {
    const { data, error } = await supabase
      .from('patient_report_pictures')
      .insert(picture)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('patient_report_pictures')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  },

  async uploadPicture(file: File, patientId: string): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${patientId}/reports/${Date.now()}.${fileExt}`;
    
    const { data, error } = await supabase.storage
      .from('patient-pictures')
      .upload(fileName, file);
    
    if (error) throw error;
    
    const { data: urlData } = supabase.storage
      .from('patient-pictures')
      .getPublicUrl(fileName);
    
    return urlData.publicUrl;
  }
};

export const bulkSMSService = {
  async getHistory(startDate?: string, endDate?: string): Promise<BulkSMSHistory[]> {
    let query = supabase
      .from('bulk_sms_history')
      .select('*')
      .order('send_date', { ascending: false });
    
    if (startDate) {
      query = query.gte('send_date', startDate);
    }
    
    if (endDate) {
      query = query.lte('send_date', endDate);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async sendBulkSMS(
    recipients: { patient_id?: string; phone_number: string; patient_name?: string }[],
    message: string,
    senderName?: string
  ): Promise<BulkSMSHistory> {
    const { data: bulkSMS, error: bulkError } = await supabase
      .from('bulk_sms_history')
      .insert({
        total_numbers: recipients.length,
        sms_count: recipients.length,
        message,
        sender_name: senderName,
        recipients: recipients,
        status: 'sent'
      })
      .select()
      .single();
    
    if (bulkError) throw bulkError;
    
    const recipientRecords = recipients.map(r => ({
      bulk_sms_id: bulkSMS.id,
      patient_id: r.patient_id,
      phone_number: r.phone_number,
      patient_name: r.patient_name,
      message,
      status: 'sent' as const
    }));
    
    const { error: recipientsError } = await supabase
      .from('sms_recipients')
      .insert(recipientRecords);
    
    if (recipientsError) throw recipientsError;
    
    return bulkSMS;
  },

  async sendToCustomNumbers(
    phoneNumbers: string[],
    message: string,
    senderName?: string
  ): Promise<BulkSMSHistory> {
    const recipients = phoneNumbers.map(phone => ({
      phone_number: phone,
      patient_name: 'Custom Number'
    }));
    
    return this.sendBulkSMS(recipients, message, senderName);
  }
};

export const reportCheckService = {
  async getAll(patientId?: string): Promise<PatientReportCheck[]> {
    let query = supabase
      .from('patient_report_checks')
      .select('*')
      .order('check_date', { ascending: false });
    
    if (patientId) {
      query = query.eq('patient_id', patientId);
    }
    
    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  },

  async create(check: Partial<PatientReportCheck>): Promise<PatientReportCheck> {
    const { data, error } = await supabase
      .from('patient_report_checks')
      .insert(check)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, updates: Partial<PatientReportCheck>): Promise<PatientReportCheck> {
    const { data, error } = await supabase
      .from('patient_report_checks')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('patient_report_checks')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};
